import { findCleanTaxCredit } from '@/SearchResults/ResultTileV2/helpers/ResultTile.helpers';
import {
  InventoryTypeId,
  VehicleTag
} from '@/SearchResults/ResultTileV2/types';
import { useSearchRequestId } from '~/contexts/InventoryContext';
import type { AvailabilityInfo, Vehicle } from '~/contracts';
import JSCookie from '~/utilities/BrowserCookies';

export function useVehicleHref(vehicle?: Vehicle) {
  return vehicle ? `/vehicle/${vehicle.vehicleId}` : undefined;
}

export const useCdnBlobUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    const { CVNA_APP_RESULT_TILE_BASE_URL = '//cdnblob.fastly.carvana.io' } =
      process.env;

    return CVNA_APP_RESULT_TILE_BASE_URL;
  }

  const { CVNA_APP_RESULT_TILE_BASE_URL = '//cdnblob.fastly.carvanatech.com' } =
    process.env;

  return CVNA_APP_RESULT_TILE_BASE_URL;
};

export const useSetSearchRequestIdCookie = () => {
  const searchRequestId = useSearchRequestId();

  return () => {
    JSCookie.set('searchRequestId', searchRequestId);
  };
};

export const findGreatDeal = (tag: Partial<VehicleTag> = {}) =>
  tag?.tagId === 104;

export const maybeHasGreatDealTag = (vehicle: Partial<Vehicle>) => {
  const { vehicleTags = [] } = vehicle;

  return vehicleTags.some(findGreatDeal);
};

export const hasCleanTaxCredit = (tileData: Partial<Vehicle>) => {
  const { vehicleTags = [] } = tileData;

  return vehicleTags.some(findCleanTaxCredit);
};

type PartialAvailabilityInfo = Partial<
  Pick<
    AvailabilityInfo,
    | 'activityType'
    | 'asSoonAsText'
    | 'daysToAvailability'
    | 'utcSoonestDateTimeIso'
    | 'routeId'
  >
>;

const defaultSoonestAvailable: PartialAvailabilityInfo = {
  activityType: undefined,
  asSoonAsText: undefined,
  daysToAvailability: undefined,
  routeId: undefined,
  utcSoonestDateTimeIso: undefined
};

export const calculateSoonestAvailable = (
  pickup?: AvailabilityInfo,
  delivery?: AvailabilityInfo
): PartialAvailabilityInfo => {
  if (pickup == null) return delivery ?? defaultSoonestAvailable;

  if (delivery == null) return pickup;

  if (pickup.drivingMiles > 150) return delivery;

  return delivery.daysToAvailability < pickup.daysToAvailability
    ? delivery
    : pickup;
};

export const getDeliveryOrPickupInfo = (vehicle: Partial<Vehicle>) => {
  // * meetupInfo is stored within deliveryInfo when isMeetupAllowed = true
  // * can to distinguish between meetup and delivery information by the activityType field
  //    * meetup: "MerchMeetup"
  //    * delivery: "LocalDelivery"
  const { pickupInfo, deliveryInfo } = vehicle;

  const soonestAvailable = calculateSoonestAvailable(pickupInfo, deliveryInfo);

  return {
    activityType: soonestAvailable?.activityType,
    dayAvailable: soonestAvailable?.asSoonAsText,
    dateAvailable: soonestAvailable?.utcSoonestDateTimeIso,
    daysTillDeliveryOrPickup: soonestAvailable?.daysToAvailability,
    routeId: soonestAvailable?.routeId
  };
};

const TWO_HOURS = 7200;

export const getPurchaseStatus = (
  vehicle: Partial<Vehicle>
): { isLocked: boolean; isOnHold: boolean } => {
  if (vehicle?.vehicleUnlockDateTime == null) {
    return {
      isLocked: false,
      isOnHold: false
    };
  }

  const timeLeft = Date.parse(vehicle?.vehicleUnlockDateTime) - +new Date();

  return {
    isLocked: Math.floor(timeLeft / 1000) >= TWO_HOURS,
    isOnHold: Math.floor(timeLeft / 1000) < TWO_HOURS
  };
};

export const getIsPreOrder = (vehicle: Partial<Vehicle>) => {
  const { isLocked, isOnHold } = getPurchaseStatus(vehicle);

  return isLocked || isOnHold
    ? false
    : vehicle?.vehicleInventoryType === InventoryTypeId.WIP;
};

export const getVehicleAvailability = (vehicle: Partial<Vehicle>) => {
  const { isOnDemand = false, vehiclePurchaseType = '' } = vehicle;
  const isReservable = vehiclePurchaseType === 'Reservable';

  return Boolean(!isOnDemand && !isReservable);
};

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];
const daysWithUniqueFormat = {
  '1': 'st',
  '2': 'nd',
  '3': 'rd'
};

export const formatDateAndMonth = (date: string): string => {
  const dateFromString = new Date(date);
  const month = months[dateFromString.getMonth()];
  const day = dateFromString.getDate().toString();
  const dayLength = day.length;
  const requiresSpecialFormatting =
    Object.keys(daysWithUniqueFormat).includes(day[dayLength - 1]) &&
    (dayLength === 1 || (dayLength === 2 && day[0] !== '1'));
  const formattedDay = requiresSpecialFormatting
    ? day + daysWithUniqueFormat[day[dayLength - 1]]
    : `${day}th`;

  return `${month} ${formattedDay}`;
};
